import { Link, NavLink } from 'react-router-dom';
import { Rocket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Brand } from '@/components/brand/Brand';
import { useAuthStore } from '@/stores/auth';
import { useCanAccess } from '@/hooks/useCanAccess';
import { useInboxSummary } from '@/hooks/inbox/useInboxSummary';
import { useInboxState } from '@/hooks/inbox/useInboxState';
import { useOnboardingProgress } from '@/hooks/useOnboarding';
import { hasPermission } from '@valiosascrm/shared';
import { Drawer, DrawerContent, DrawerTitle, Progress } from '@/v2/design-system/primitives';
import { getNavSections, NAV_HELP, type NavEntry } from './nav';

const badgeClass =
  'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-sober-primary-fg px-1.5 text-so-12 font-bold text-white';

/**
 * Story 27.35 — Inbox badge reflects the REAL unread total, but only when the
 * inbox is READY (connected). v1 gates `useInboxSummary(isReady)`; mirroring it
 * here avoids showing a DB-wide count (e.g. "1000") while the list is empty
 * because WhatsApp is disconnected. Capped at "99+".
 */
function InboxBadge({ compact = false }: { compact?: boolean }) {
  const isReady = useInboxState().phase === 'READY';
  const { data } = useInboxSummary(isReady);
  const n = isReady ? (data?.totalUnread ?? 0) : 0;
  if (!n) return null;
  return (
    <span className={cn(badgeClass, compact && 'lg:absolute lg:right-1 lg:top-1 lg:h-4 lg:min-w-4 lg:px-1 lg:text-[10px]')}>
      {n > 99 ? '99+' : n}
    </span>
  );
}

function NavItemLink({
  item,
  onNavigate,
  compact = false,
}: {
  item: NavEntry;
  onNavigate?: () => void;
  compact?: boolean;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      title={compact ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'relative flex min-h-11 items-center rounded-so-md border border-transparent py-2.5 text-so-14 font-bold transition-all duration-150',
          compact ? 'lg:justify-center lg:px-2' : 'gap-3 px-3',
          isActive
            ? 'border-sober-outline bg-sober-primary-bg text-sober-primary shadow-so-card'
            : 'text-sober-secondary hover:border-sober-outline hover:bg-sober-app hover:text-sober-text'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              aria-hidden
              className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-sober-primary"
            />
          )}
          <Icon className="size-5 shrink-0" />
          <span className={cn('flex-1 truncate', compact && 'lg:sr-only')}>{item.label}</span>
          {item.id === 'inbox' ? (
            <InboxBadge compact={compact} />
          ) : item.count ? (
            <span className={cn(badgeClass, compact && 'lg:absolute lg:right-1 lg:top-1 lg:h-4 lg:min-w-4 lg:px-1 lg:text-[10px]')}>
              {item.count}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

/** Gated by useCanAccess only when the user is authenticated (demo/foundation shows all). */
function GatedNavItem({
  item,
  onNavigate,
  compact = false,
}: {
  item: NavEntry;
  onNavigate?: () => void;
  compact?: boolean;
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const canAccess = item.permissionsAny?.some((permission) => role ? hasPermission(role, permission) : false)
    ?? (item.permission && role ? hasPermission(role, item.permission) : true);
  if (isAuthenticated && !canAccess) return null;
  return <NavItemLink item={item} onNavigate={onNavigate} compact={compact} />;
}

function renderItem(item: NavEntry, onNavigate?: () => void, compact = false) {
  return item.permission || item.permissionsAny ? (
    <GatedNavItem key={item.id} item={item} onNavigate={onNavigate} compact={compact} />
  ) : (
    <NavItemLink key={item.id} item={item} onNavigate={onNavigate} compact={compact} />
  );
}

export interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
  /** onboarding completion 0-100 */
  onboardingProgress?: number;
  /** Desktop theatre mode: collapse the shell to icons so the main canvas owns the screen. */
  compact?: boolean;
}

export function Sidebar({ mobileOpen, onClose, onboardingProgress, compact = false }: SidebarProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userRole = useAuthStore((s) => s.user?.role);
  const canManageOnboarding = useCanAccess('settings:manage');
  const showOnboarding = !isAuthenticated || canManageOnboarding;
  const { data: onboarding } = useOnboardingProgress({
    enabled: isAuthenticated && canManageOnboarding,
  });
  // Progresso REAL: 100 se isComplete, senão proporção de steps concluídos.
  // NUNCA derivar de currentStep nem de um default fixo (PV-01: sidebar mostrava 60% eterno).
  const computedProgress = onboarding
    ? onboarding.isComplete
      ? 100
      : Math.round(
          (onboarding.steps.filter((s) => s.completed).length / Math.max(onboarding.steps.length, 1)) * 100,
        )
    : 0;
  const progress = onboardingProgress ?? computedProgress;
  const content = (
    <>
        {/* Onda 0 (UX-13): a marca leva ao Início, no desktop e na gaveta. */}
        <Link
          to="/dashboard"
          onClick={onClose}
          aria-label="ValiosasCRM — página inicial"
          className="block rounded-so-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary-fg"
        >
          <Brand
            subtitle="Clínica Estética"
            size={compact ? 'sm' : 'md'}
            className={cn(compact && 'lg:justify-center [&>span:last-child]:lg:sr-only')}
          />
        </Link>

        {showOnboarding && (
          <NavLink
            to="/primeiros-passos"
            onClick={onClose}
            title={compact ? `Primeiros Passos (${progress}%)` : undefined}
            className={({ isActive }) =>
              cn(
                'group flex items-center gap-3 rounded-so-lg border p-3 transition-all duration-150',
                compact && 'lg:justify-center lg:p-2.5',
                isActive
                  ? 'border-sober-primary bg-sober-surface shadow-so-card'
                  : 'border-transparent bg-sober-app hover:border-sober-outline hover:bg-sober-surface hover:shadow-so-card'
              )
            }
            aria-label="Primeiros Passos"
          >
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-so-md bg-sober-primary-bg text-sober-primary transition-transform group-hover:-translate-y-0.5">
              <Rocket className="size-4" />
            </div>
            <div className={cn('min-w-0 flex-1', compact && 'lg:sr-only')}>
              <div className="flex items-center justify-between gap-2">
                <div className="truncate text-so-12 font-bold text-sober-text">Primeiros Passos</div>
                <div className="shrink-0 text-so-12 font-bold text-sober-primary">{progress}%</div>
              </div>
              <Progress className="mt-1.5" value={progress} variant="blue" />
            </div>
          </NavLink>
        )}

        {/* Sections — 45.5: ordem por role (professional agenda-first; demais roles intocadas) */}
        <nav className={cn('flex flex-1 flex-col', compact ? 'gap-3' : 'gap-5')}>
          {getNavSections(userRole).map((section, i) => (
            <div key={section.title ?? `section-${i}`} className="flex flex-col gap-1">
              {section.title && (
                <div className={cn('px-3 pb-1 text-so-12 font-bold uppercase tracking-wide text-sober-secondary', compact && 'lg:sr-only')}>
                  {section.title}
                </div>
              )}
              {section.items.map((item) => renderItem(item, onClose, compact))}
            </div>
          ))}

          <div className="mt-auto flex flex-col gap-1 border-t border-sober-outline pt-3">
            {renderItem(NAV_HELP, onClose, compact)}
          </div>
        </nav>
    </>
  );
  return <>
    <aside aria-label="Navegação principal" className={cn(
      'hidden border-r border-sober-outline bg-sober-surface lg:sticky lg:top-0 lg:flex lg:h-dvh-safe lg:shrink-0 lg:flex-col lg:overflow-y-auto',
      compact ? 'lg:w-[72px] lg:gap-3 lg:p-3' : 'gap-5 lg:w-64 lg:p-5',
    )}>{content}</aside>
    <Drawer open={mobileOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent side="left" className="w-[280px] gap-5 overflow-y-auto p-4 pt-16" onCloseAutoFocus={(event) => {
        event.preventDefault(); document.getElementById('main-navigation-trigger')?.focus();
      }}>
        <DrawerTitle className="sr-only">Navegação principal</DrawerTitle>
        {content}
      </DrawerContent>
    </Drawer>
  </>;
}
